import { motion } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FaLinkedin } from 'react-icons/fa'
import { HiOutlineDownload } from 'react-icons/hi'
import { RiDiscordFill } from 'react-icons/ri'
import { RxCross2, RxHamburgerMenu } from 'react-icons/rx'
import { VscGithubAlt } from 'react-icons/vsc'
import { useLocation, useNavigate } from 'react-router'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import useSound from 'use-sound'
import Doing from './Doing'

const pathnameOffsets = {
  '/': 0,
  '/projects': 39,
  '/skills': 78,
  '/more': 117,
}

function Nav() {
  const history = useNavigate()
  const { pathname } = useLocation()

  const [playSwitchPageSound] = useSound('/p-static/sounds/switch-page.mp3')

  const [dragYOffset, setDragYOffset] = useState(0)
  const [openOnMobile, setOpenOnMobile] = useState(false)
  const [presenceActive, setPresenceActive] = useState(false)
  const dragConstraintsRef = useRef(null)

  useEffect(() => {
    if (openOnMobile)
      setOpenOnMobile(false)
    playSwitchPageSound()
  }, [pathname])

  const pageIndicatorOffset = useMemo(
    () => (pathname ? pathnameOffsets[pathname] ?? -180 : 0),
    [pathname],
  )

  const pageIndicatorOffsetWithDecoration = useMemo(
    () => 71 + 33 + pageIndicatorOffset - dragYOffset,
    [pageIndicatorOffset, dragYOffset],
  )

  const onPageIndicatorDragEnd = useCallback(
    (_event, info) => {
      const goal = pageIndicatorOffset + info.offset.y

      const closest = Object.entries(pathnameOffsets).reduce(
        ([prevPath, prevOffset], [curPath, curOffset]) => {
          return Math.abs(curOffset - goal) < Math.abs(prevOffset - goal)
            ? [curPath, curOffset]
            : [prevPath, prevOffset]
        },
      )

      if (closest[0] === pathname)
        return

      setDragYOffset(dragYOffset + info.offset.y + info.velocity.y)
      history.push(closest[0])
    },
    [history, pageIndicatorOffset, dragYOffset, pathname],
  )

  const toggleMobileMenu = useCallback(
    () => setOpenOnMobile(!openOnMobile),
    [openOnMobile],
  )
  return (
    <>
      <MobileHeader>
        <Title>Ruhan Rouf</Title>
        {openOnMobile
          ? (
              <RxCross2 onClick={toggleMobileMenu} />
            )
          : (
              <RxHamburgerMenu onClick={toggleMobileMenu} />
            )}
      </MobileHeader>
      <Container $openOnMobile={openOnMobile}>
        {!openOnMobile
          ? (
              <PageIndicator
                whileHover={{ width: 3 }}
                drag="y"
                onDragEnd={onPageIndicatorDragEnd}
                dragConstraints={dragConstraintsRef}
                animate={{ top: pageIndicatorOffsetWithDecoration }}
              />
            )
          : null}
        <Items>
          {!openOnMobile
            ? (
                <Row>
                  <Title>Ruhan Rouf</Title>
                </Row>
              )
            : null}
          <Row>
            <Resume
              href="https://rxresu.me/ruhannn/main"
              target="_blank"
            >
              <HiOutlineDownload />
              {' '}
              Resume
            </Resume>
          </Row>

          <div ref={dragConstraintsRef}>
            <Page
              active={pathname === '/' ? 1 : 0}
              to="/"
            >
              About Me
            </Page>
            <Page
              active={pathname === '/projects' ? 1 : 0}
              to="/projects"
            >
              My Projects
            </Page>
            <Page
              active={pathname === '/skills' ? 1 : 0}
              to="/skills"
            >
              Skill Set
            </Page>
            <Page
              active={pathname === '/more' ? 1 : 0}
              to="/more"
            >
              more + contact
            </Page>
          </div>

          <Icons>
            <span
              onClick={() =>
                window.open('https://discord.com/users/819191621676695563')}
            >
              <RiDiscordFill />
            </span>
            <span onClick={() => window.open('https://github.com/Ruhannn')}>
              <VscGithubAlt />
            </span>
          </Icons>
          <Doing
            style={{ display: presenceActive ? 'block' : 'none' }}
            setActive={setPresenceActive}
          />
        </Items>
      </Container>
    </>
  )
}

const Container = styled.aside`
  display: inline-block;
  box-sizing: border-box;
  flex-direction: column;
  padding: 2rem;
  position: fixed;
  top: 0;
  left: 0;
  width: 15rem;
  border-right: 1px solid hsl(var(--primary-800));
  height: 100vh;

  @media (max-width: 850px) {
    display: ${({ $openOnMobile }) => ($openOnMobile ? 'block' : 'none')};
    background-color: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(7px);
    z-index: 1;
    top: 65px;
    width: 100%;
    height: calc(100% - 65px);
  }
`

const MobileHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: fixed;
  top: 0;
  display: flex;
  padding: 2rem;
  box-sizing: border-box;
  width: 100%;
  height: 65px;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  border-bottom: 1px solid hsl(var(--primary-800));
  flex-shrink: 0;
  z-index: 1;

  svg {
    margin-left: auto;
    cursor: pointer;
    color: #ccc;
  }

  @media (min-width: 850px) {
    display: none;
  }
`

const PageIndicator = styled(motion.div)`
  width: 1px;
  height: 39px;
  background-color: #fff;
  position: absolute;
  right: -1px;
  cursor: pointer;
`

const Items = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`

const Title = styled.div`
  font-weight: 600;
  padding: 10px 0px;
`

const Resume = styled.a`
  width: 100%;
  display: flex;
  align-items: center;
  font-weight: 500;
  height: 19px;
  font-size: 14px;
  margin-bottom: 15px;
  user-select: none;

  color: white;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }

  svg:first-child {
    height: 18px;
    width: 18px;
    margin-right: 10px;
    color: #3c71e4;
  }
`

const Page = styled(Link)`
  color: ${({ active }) => (active ? '#fff' : '#ccc')};
  padding: 10px 0px;
  display: flex;

  &:hover {
    /* background-color: #fff; */
    color: #fff;
  }
`

const Icons = styled.div`
  margin-top: auto;
  color: #ccc;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-evenly;
  padding: 1rem 0;

  svg {
    width: 20px;
    height: 20px;
    cursor: pointer;
    color: #ccc;

    &:hover {
      color: #fff;
    }
  }
`

export default Nav
